import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { BookOpen, Loader2 } from "lucide-react";
import { Wordmark, TaglineBar } from "../components/Brand";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState("login");
  const invited = !!localStorage.getItem("khm_ref");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/status").then((r) => {
      setStatus(r.data);
      if (r.data.public_registration && new URLSearchParams(window.location.search).get("ref")) setMode("register");
    }).catch(() => {});
  }, []);
  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", { email, password, name });
      setUser(data);
      navigate("/app");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden border-r border-amber-500/20 bg-slate-950 flex-col items-center justify-center gap-8 px-12 py-10 min-h-screen">
        <div className="smoke" />
        <div className="relative flex flex-col items-center gap-8">
          <div className="emblem-ring w-80 h-80 xl:w-96 xl:h-96 [@media(max-height:760px)]:w-72 [@media(max-height:760px)]:h-72 flex items-center justify-center bg-black/40"><span className="spark -top-[3px] left-1/2 -translate-x-1/2" /><Wordmark size="lg" testId="login-wordmark" /></div>
          <TaglineBar />
        </div>
        <div className="relative text-center mt-4" data-testid="login-private-caption">
          <p className="eyebrow mb-3">Private studio</p>
          <p className="font-display text-xl text-slate-200 leading-snug">Every word you wrote is kept. Every change is traceable.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="login-form">
          <Link to="/" className="flex items-center gap-3 text-amber-400 mb-8" data-testid="login-home-link"><BookOpen size={20} /><Wordmark size="sm" testId="login-home-wordmark" /></Link>
          <h1 className="font-display text-4xl">{mode === "login" ? "Sign in" : "Create account"}</h1>
          <p className="text-sm text-slate-400">{mode === "login" ? "Owner and invited members only." : "Public registration is open."}</p>
          {invited && <p className="text-xs text-amber-200 border border-amber-500/40 bg-amber-950/30 rounded-md px-3 py-2" data-testid="login-invite-note">You were invited by a fellow author — you'll get bonus credits on your first credit pack.{status && !status.public_registration && " Sign in to accept the invite. No account yet? Ask the site owner to create one for you, then sign in here."}</p>}
          {mode === "register" && <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />}
          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-500" data-testid="login-submit-button">
            {busy && <Loader2 className="animate-spin mr-2" size={16} />}{mode === "login" ? "Sign in" : "Create account"}
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-500"><span className="h-px bg-slate-800 flex-1" />or<span className="h-px bg-slate-800 flex-1" /></div>
          <Button type="button" variant="outline" onClick={google} className="w-full border-slate-700" data-testid="google-login-button">Continue with Google</Button>
          <p className="text-xs text-slate-500">Google sign-in works only for accounts the owner has added or linked.</p>
          {status?.public_registration && (
            <button type="button" className="text-sm text-amber-400" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-register">
              {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
